
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (target: string) => {
    if (location.pathname === '/admin') {
      // If on admin page, navigate to home page
      if (target === '#home') {
        navigate('/');
      }
    } else {
      // If on home page, scroll to section
      if (target.startsWith('#')) {
        const element = document.querySelector(target);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate(target);
      }
    }
    setIsMenuOpen(false);
  };

  const logout = () => {
    localStorage.removeItem('santa-helper-user');
    sessionStorage.removeItem('santa-helper-user');
    navigate('/login');
  };

  const currentUser = JSON.parse(
    localStorage.getItem('santa-helper-user') || 
    sessionStorage.getItem('santa-helper-user') || 
    '{}'
  );

  // Mock notifications - in a real app, these would come from your backend
  const notifications = [
    {
      id: 1,
      message: "Gift purchased successfully",
      time: "2 minutes ago",
      type: "success"
    },
    {
      id: 2,
      message: "Wishlist shared with family",
      time: "1 hour ago",
      type: "info"
    },
    {
      id: 3,
      message: "New item added to wishlist",
      time: "3 hours ago",
      type: "info"
    }
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-40">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="text-2xl font-bold text-red-600 cursor-pointer flex items-center"
              style={{ fontFamily: '"Pacifico", serif' }}
              key={Date.now()} // Force re-render to clear cache
            >
              <span className="text-3xl mr-2">🎅</span>
              Santa's Helper
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => handleNavigation('#home')}
              className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer"
            >
              Home
            </button>
            {location.pathname === '/' && (
              <button
                onClick={() => handleNavigation('#gifts')}
                className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer"
              >
                My Wishlist
              </button>
            )}
            {currentUser.role === 'admin' && (
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer"
              >
                Admin
              </button>
            )}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {currentUser.id ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600 hidden sm:block">
                  Welcome, {currentUser.firstName}!
                </span>
                
                {/* Notification Box */}
                <div className="relative">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="w-8 h-8 flex items-center justify-center cursor-pointer rounded-full hover:bg-gray-100 relative"
                  >
                    <i className="ri-notification-line text-gray-600"></i>
                    {notifications.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {notifications.length}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {showNotifications && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                      <div className="p-4 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-800">Notifications</h3>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            No new notifications
                          </div>
                        ) : (
                          notifications.map(notification => (
                            <div key={notification.id} className="p-4 border-b border-gray-100 hover:bg-gray-50">
                              <div className="flex items-start">
                                <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${
                                  notification.type === 'success' ? 'bg-green-500' : 'bg-blue-500'
                                }`}></div>
                                <div className="flex-1">
                                  <p className="text-sm text-gray-800">{notification.message}</p>
                                  <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="p-3 border-t border-gray-200">
                        <button 
                          onClick={() => setShowNotifications(false)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <Button size="sm" variant="outline" onClick={logout}>
                  <i className="ri-logout-box-line mr-1"></i>
                  Logout
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={() => navigate('/login')}>
                <i className="ri-login-box-line mr-1"></i>
                Login
              </Button>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer rounded-lg hover:bg-gray-100"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-3">
              <button
                onClick={() => handleNavigation('#home')}
                className="text-left text-gray-700 hover:text-red-600 transition-colors cursor-pointer py-2"
              >
                Home
              </button>
              {location.pathname === '/' && (
                <button
                  onClick={() => handleNavigation('#gifts')}
                  className="text-left text-gray-700 hover:text-red-600 transition-colors cursor-pointer py-2"
                >
                  My Wishlist
                </button>
              )}
              {currentUser.role === 'admin' && (
                <button
                  onClick={() => handleNavigation('/admin')}
                  className="text-left text-gray-700 hover:text-red-600 transition-colors cursor-pointer py-2"
                >
                  Admin
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
